/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceCreateGitHubPullRequestStep" />
import * as React from "react";
import { IBoardBadgeService } from "Agile/Board/Badge/Services";
import { SelectionMode } from "OfficeFabric/Selection";
import { localeFormat } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { PickListDropdown, IPickListSelection } from "VSSUI/PickList";
import {
    GitHubAppFreSamplePullRequestDataProviderRequest,
    GitHubAppFreSamplePullRequestDataProviderResponse
} from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { FirstRunExperienceStep } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStep";
import { StepStatus } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStep.Props";
import { IFirstRunExperienceCreateGitHubPullRequestStepProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceCreateGitHubPullRequestStep.Props";
import { DocUrls } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import { publishGitHubIntegrationTelemetry, GitHubIntegrationCIActions } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

const GitHubAppFreSamplePullRequestDataProviderId = "ms.vss-work-web.github-fre-sample-pull-request-data-provider";

export interface IFirstRunExperienceCreateGitHubPullRequestStepState {
    selectedRepo: string;
}

export class FirstRunExperienceCreateGitHubPullRequestStep extends VssComponent<
    IFirstRunExperienceCreateGitHubPullRequestStepProps,
    IFirstRunExperienceCreateGitHubPullRequestStepState
> {
    private _startTimeStamp: number;

    constructor(props: IFirstRunExperienceCreateGitHubPullRequestStepProps, context: IVssComponentContext) {
        super(props, context);

        const { repoNames } = this.props;
        this.state = {
            selectedRepo: repoNames[0]
        };
    }

    public componentDidMount(): void {
        this._startTimeStamp = Date.now();
    }

    public render(): JSX.Element {
        const { stepId, activeStepId } = this.props;
        return (
            <FirstRunExperienceStep
                stepId={stepId}
                activeStepId={activeStepId}
                onRenderTitle={this._onRenderTitle}
                onRenderContent={this._onRenderContent}
                onRenderError={this._onRenderError}
                onRenderHelpLinks={this._onRenderHelpLinks}
                actionButtonText={Resources.FirstRunExperience_CreateGitHubPullRequestStep_ActionButtonText}
                stepProcessingText={Resources.FirstRunExperience_CreateGitHubPullRequestStep_ProcessingText}
                onActionButtonClicked={this._onActionButtonClicked}
            />
        );
    }

    private readonly _onRenderTitle = (status: StepStatus): JSX.Element | string => {
        const { boardInfo } = this.props;
        const { defaultWorkItemType } = boardInfo;
        return status === StepStatus.Succeeded
            ? localeFormat(Resources.FirstRunExperience_CreateGitHubPullRequestStep_SuccessTitle, defaultWorkItemType)
            : localeFormat(Resources.FirstRunExperience_CreateGitHubPullRequestStep_DefaultTitle, defaultWorkItemType);
    };

    private readonly _onRenderContent = (status: StepStatus): JSX.Element | string | undefined => {
        const { repoNames } = this.props;
        return repoNames.length > 1 && (status === StepStatus.Started || status === StepStatus.Processing) ? (
            <div className="repos-dropdown">
                <span className="label body-m">{Resources.FirstRunExperience_CreateGitHubPullRequestStep_RepoDropdownLabel}</span>
                <PickListDropdown
                    className="dropdown"
                    selectionMode={SelectionMode.single}
                    selectedItems={[this.state.selectedRepo]}
                    getPickListItems={() => repoNames}
                    onSelectionChanged={(selection: IPickListSelection<string>) => this.setState({ selectedRepo: selection.selectedItems[0] })}
                />
            </div>
        ) : (
            undefined
        );
    };

    private readonly _onRenderError = (): JSX.Element | string | undefined => {
        return Resources.FirstRunExperience_CreateGitHubPullRequestStep_Error;
    };

    private readonly _onRenderHelpLinks = (status: StepStatus): JSX.Element | undefined => {
        return status === StepStatus.Failed ? (
            <Link className="step-help-link" target="_blank" href={DocUrls.GitHubAndAzureBoards_LinkPullRequest}>
                {Resources.FirstRunExperience_CreateGitHubPullRequestStep_Error_LinkPullRequestDoc_Link}
            </Link>
        ) : (
            undefined
        );
    };

    private readonly _onActionButtonClicked = async (): Promise<StepStatus> => {
        const { projectId, boardInfo, workItem, onStepProcessing, onStepSucceeded, onStepFailed } = this.props;
        const { teamId, backlogLevelId, boardId } = boardInfo;
        const { selectedRepo } = this.state;

        try {
            if (!selectedRepo || !workItem || !projectId || !teamId || !backlogLevelId || !boardId) {
                throw new Error(
                    `Unexpected input for creating sample pull request. ProjectId=${projectId}, TeamId=${teamId}, BacklogLevelId=${backlogLevelId}, BoardId=${boardId}, HasWorkItem=${!!workItem}, HasSelectedRepo=${!!selectedRepo}`
                );
            }

            onStepProcessing && onStepProcessing();

            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const result = await contributionService.getDataAsync<
                GitHubAppFreSamplePullRequestDataProviderResponse,
                GitHubAppFreSamplePullRequestDataProviderRequest
            >(
                GitHubAppFreSamplePullRequestDataProviderId,
                {
                    repoNameWithOwner: selectedRepo,
                    workItemId: workItem.id,
                    teamId: teamId,
                    backlogLevelId: backlogLevelId,
                    boardId: boardId
                },
                true /*ignoreCache*/
            );

            if (result!.errorMessage) {
                throw new Error(result!.errorMessage);
            }

            this._enableAnonymousAccessToBoardBadge();

            onStepSucceeded && onStepSucceeded(Date.now() - this._startTimeStamp);
            return StepStatus.Succeeded;
        } catch (error) {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_CREATE_LINK_GITHUB_PULL_REQUEST, { error: error.message });
            onStepFailed && onStepFailed(error.message, Date.now() - this._startTimeStamp);
            return StepStatus.Failed;
        }
    };

    private async _enableAnonymousAccessToBoardBadge(): Promise<void> {
        try {
            const { projectId, boardInfo } = this.props;
            const { teamId, boardId } = boardInfo;

            const boardBadgeService = this.context.pageContext.getService<IBoardBadgeService>("IBoardBadgeService");
            const isPublic = await boardBadgeService.setIsPublicOption(projectId, teamId, boardId, true);
            if (!isPublic) {
                throw new Error("Failed to set board badge anonymous access.");
            }
        } catch (error) {
            // Instead of throwing, log the telemetry only for updating the anonymouse access setting for board badge since it doesn't impact major FRE flow.
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_SET_BOARD_BADGE_ANONYMOUS_ACCESS, {
                error: error.message
            });
        }
    }
}
